import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAnalyticsStore from '../stores/useAnalyticsStore';
import DashboardLayout from '../components/DashboardLayout';
import useAuthStore from '../stores/useAuthStore';

const StudentProgressPage = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { courseProgress, fetchCourseProgress, isLoading, error } = useAnalyticsStore();

  useEffect(() => {
    fetchCourseProgress();
  }, [fetchCourseProgress]);

  const getTotalLessons = (course) => {
    return course.modules?.reduce((total, module) => total + (module.lessons?.length || 0), 0) || 0;
  };

  const getCompletedLessonsCount = (course, progress) => {
    if (!progress?.completedLessons || !Array.isArray(progress.completedLessons)) return 0;
    let count = 0;
    for (const module of course.modules || []) {
      for (const lesson of module.lessons || []) {
        if (progress.completedLessons.includes(lesson._id.toString())) {
          count++;
        }
      }
    }
    return count;
  };

  const getProgressPercentage = (course, progress) => {
    const total = getTotalLessons(course);
    if (total === 0) return 0;
    const completed = getCompletedLessonsCount(course, progress);
    return Math.round((completed / total) * 100);
  };

  if (isLoading) {
    return (
      <DashboardLayout userRole={user.role}>
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">Failed to load progress</h2>
          <p className="text-neutral-600 dark:text-neutral-400 mb-4">{error}</p>
          <button
            onClick={() => fetchCourseProgress()}
            className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <DashboardLayout userRole={user.role}>
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">My Learning Progress</h1>
            <p className="text-neutral-600 dark:text-neutral-400 mt-2">
              Track your progress across all enrolled courses
            </p>
          </div>

          {courseProgress?.length > 0 ? (
            courseProgress?.map((course, index) => (
              <div key={index} className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm  dark:border-neutral-700 p-6 cursor-pointer hover:shadow-md transition-shadow border-b border-dashed border-t border-l border-r border-neutral-200"
               onClick={() => navigate(`/student/learning/${course?.course?._id}`)}
              >
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-medium text-neutral-900 dark:text-white">{course.course?.title}</h3>
                  <span className="px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs rounded-full">
                    {course.course?.category?.name}
                  </span>
                </div>
                <div className="mb-4">
                  <div className="flex items-center mb-2">
                    <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2 mr-2">
                      <div
                        className="bg-primary-500 h-2 rounded-full"
                        style={{ width: `${course.courseProgress || 0}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-neutral-900 dark:text-white ml-2">
                      {course.courseProgress || 0}%
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-neutral-600 dark:text-neutral-400">
                  <div>
                    <p className="font-medium text-neutral-700 dark:text-neutral-300">Lessons</p>
                    <p> {course.completedLessons?.length || 0} / {course.course.totalLessons || 0} completed</p>
                  </div>
                  <div>
                    <p className="font-medium text-neutral-700 dark:text-neutral-300">Last Accessed</p>
                    <p>{new Date(course.lastAccessed).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🎓</div>
              <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">No enrolled courses yet</h3>
              <p className="text-neutral-600 dark:text-neutral-400">
                Enroll in a course to start tracking your progress.
              </p>
              <button
                onClick={() => navigate('/courses')}
                className="mt-4 px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                Browse Courses
              </button>
            </div>
          )}

        </div>
      </div>
    </DashboardLayout>
  );
};

export default StudentProgressPage;